import React, { useState, useEffect } from 'react';
import { X, ExternalLink, Eye } from 'lucide-react';
import { Occurrence, OccurrenceStatus } from '../types';
import { occurrenceService } from '../occurrenceService';
import { useEscapeKey } from '../../../../shared/useGlobalModalEscape';
import { useDialog } from '../../../../shared/DialogContext';
import { ReportDetailsModal } from '../../../../components/ReportDetailsModal';
import { ingestionService } from '../../../ingestion/ingestionService';
import { SafetyReport } from '../../../ingestion/types';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

interface OccurrenceDetailModalProps {
  occurrenceId: string;
  onClose: () => void;
  onUpdated: () => void;
}

export const OccurrenceDetailModal: React.FC<OccurrenceDetailModalProps> = ({
  occurrenceId,
  onClose,
  onUpdated,
}) => {
  useEscapeKey(onClose);
  const { showError, showWarning } = useDialog();

  const [occ, setOcc] = useState<Occurrence | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'reports' | 'assignments' | 'evidence' | 'comments' | 'timeline' | 'eccairs'>('overview');

  // Action dialog states
  const [actionReason, setActionReason] = useState<string>('');
  const [currentAction, setCurrentAction] = useState<OccurrenceStatus | null>(null);
  const [newComment, setNewComment] = useState<string>('');
  const [newReportId, setNewReportId] = useState<string>('');
  const [newReportNumber, setNewReportNumber] = useState<string>('');
  const [isPrimaryReport, setIsPrimaryReport] = useState<boolean>(false);
  const [evidenceName, setEvidenceName] = useState<string>('');
  const [evidenceKey, setEvidenceKey] = useState<string>('');
  const [assignUser, setAssignUser] = useState<string>('');
  const [assignName, setAssignName] = useState<string>('');
  const [assignDept, setAssignDept] = useState<string>('Flight Operations');
  const [assignRole, setAssignRole] = useState<string>('LEAD_INVESTIGATOR');
  const [eccairsXml, setEccairsXml] = useState<string>('');
  const [eccairsLoading, setEccairsLoading] = useState<boolean>(false);
  const [inspectingReport, setInspectingReport] = useState<SafetyReport | null>(null);
  const [loadingReportDetails, setLoadingReportDetails] = useState<boolean>(false);

  const linkedReportsList = occ?.linkedReports || [];
  const {
    sortedItems: sortedLinkedReports,
    sortField: repSortField,
    sortDirection: repSortDirection,
    requestSort: requestRepSort,
  } = useTableSort(linkedReportsList);

  const handleOpenReportDetails = async (reportIdOrNumber: string) => {
    try {
      setLoadingReportDetails(true);
      const rep = await ingestionService.getReportById(reportIdOrNumber);
      if (rep) {
        setInspectingReport(rep);
      } else {
        showWarning(`Report #${reportIdOrNumber} could not be retrieved.`);
      }
    } catch (err: any) {
      showError(err, 'Failed to open report details');
    } finally {
      setLoadingReportDetails(false);
    }
  };

  const handleNavigateToSafetyReports = (reportIdOrNumber?: string) => {
    onClose();
    const params = new URLSearchParams();
    params.set('tab', 'ingestion');
    if (reportIdOrNumber) {
      params.set('reportId', reportIdOrNumber);
    }
    const newUrl = `?${params.toString()}`;
    window.history.pushState({ tab: 'ingestion' }, '', newUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const data = await occurrenceService.getOccurrenceById(occurrenceId);
      setOcc(data);
    } catch (err: any) {
      showError(err, 'Error Loading Occurrence');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [occurrenceId]);

  const handleTransition = async (status: OccurrenceStatus) => {
    if (!occ) return;
    if ((status === 'CLOSED' || status === 'REOPENED') && !actionReason.trim()) {
      showWarning('Please provide a mandatory justification reason for this transition.', 'Justification Required');
      return;
    }

    try {
      if (status === 'VERIFIED') {
        await occurrenceService.verifyOccurrence(occ.id, actionReason || 'Verified by Safety Officer');
      } else if (status === 'UNDER_INVESTIGATION') {
        await occurrenceService.triggerInvestigation(occ.id, actionReason || 'Formal investigation launched');
      } else if (status === 'CLOSED') {
        await occurrenceService.closeOccurrence(occ.id, actionReason);
      } else if (status === 'REOPENED') {
        await occurrenceService.reopenOccurrence(occ.id, actionReason);
      }
      setActionReason('');
      setCurrentAction(null);
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      showError(err, 'Occurrence Transition Failed');
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!occ || !newComment.trim()) return;
    try {
      await occurrenceService.addComment(occ.id, newComment);
      setNewComment('');
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Failed to Add Comment');
    }
  };

  const handleLinkReport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!occ || !newReportId.trim()) return;
    try {
      await occurrenceService.linkReport(
        occ.id,
        newReportId.trim(),
        newReportNumber.trim() || newReportId.trim(),
        isPrimaryReport
      );
      setNewReportId('');
      setNewReportNumber('');
      setIsPrimaryReport(false);
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Failed to Link Safety Report');
    }
  };

  const handleUnlinkReport = async (reportId: string) => {
    if (!occ) return;
    try {
      await occurrenceService.unlinkReport(occ.id, reportId);
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Failed to Unlink Report');
    }
  };

  const handleAddEvidence = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!occ || !evidenceName.trim()) return;
    try {
      await occurrenceService.addEvidence(
        occ.id,
        evidenceName.trim(),
        102400,
        'application/pdf',
        evidenceKey.trim() || `evidence/${evidenceName.trim()}`
      );
      setEvidenceName('');
      setEvidenceKey('');
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Failed to Upload Evidence');
    }
  };

  const handleAddAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!occ || !assignName.trim()) return;
    try {
      await occurrenceService.assignInvestigator(
        occ.id,
        assignUser.trim() || 'USER-INV',
        assignName.trim(),
        assignDept,
        assignRole
      );
      setAssignUser('');
      setAssignName('');
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Failed to Assign Investigator');
    }
  };

  const loadEccairsXml = async () => {
    if (!occ) return;
    try {
      setEccairsLoading(true);
      const res = await fetch(`/api/occurrences/${occ.id}/eccairs?format=xml`);
      const xml = await res.text();
      setEccairsXml(xml);
    } catch (err: any) {
      setEccairsXml(`Error loading ECCAIRS XML: ${err.message}`);
    } finally {
      setEccairsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'eccairs') {
      loadEccairsXml();
    }
  }, [activeTab]);

  if (loading || !occ) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ background: '#fff', padding: '30px', borderRadius: '8px', minWidth: '300px', textAlign: 'center' }}>
          Loading occurrence details...
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-w-5xl" style={{ height: '90vh' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#0284c7' }}>{occ.occurrenceNumber}</span>
              <span style={{ fontSize: '12px', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>
                {occ.status}
              </span>
              {occ.isMandatory && (
                <span style={{ fontSize: '11px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #f87171', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>
                  MOR 72h Clock
                </span>
              )}
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b', marginTop: '4px' }}>{occ.title}</div>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#ffffff', padding: '0 24px', gap: '8px' }}>
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'reports', label: `Linked Reports (${occ.linkedReports?.length || 0})` },
            { id: 'assignments', label: `Assignments (${occ.assignments?.length || 0})` },
            { id: 'evidence', label: `Evidence (${occ.evidence?.length || 0})` },
            { id: 'comments', label: `Discussion (${occ.comments?.length || 0})` },
            { id: 'timeline', label: `Timeline (${occ.history?.length || 0})` },
            { id: 'eccairs', label: 'ECCAIRS 2 / ADREP XML' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #0284c7' : '2px solid transparent',
                color: activeTab === tab.id ? '#0284c7' : '#64748b',
                padding: '12px 14px',
                fontSize: '13px',
                fontWeight: activeTab === tab.id ? 600 : 500,
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1, fontSize: '13px', color: '#334155' }}>
          {activeTab === 'overview' && (
            <div>
              {/* Quick Actions Bar */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', background: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Workflow Actions:</span>
                {occ.status === 'NEW' && (
                  <button onClick={() => handleTransition('VERIFIED')} style={{ background: '#16a34a', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
                    ✓ Verify Occurrence
                  </button>
                )}
                {(occ.status === 'VERIFIED' || occ.status === 'NEW') && (
                  <button onClick={() => handleTransition('UNDER_INVESTIGATION')} style={{ background: '#9333ea', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
                    🔍 Launch Investigation
                  </button>
                )}
                {occ.status !== 'CLOSED' && (
                  <button onClick={() => setCurrentAction('CLOSED')} style={{ background: '#475569', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
                    🔒 Close Occurrence
                  </button>
                )}
                {occ.status === 'CLOSED' && (
                  <button onClick={() => setCurrentAction('REOPENED')} style={{ background: '#e11d48', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
                    🔄 Reopen Occurrence
                  </button>
                )}
              </div>

              {/* Action reason dialog */}
              {currentAction && (
                <div style={{ marginBottom: '20px', background: '#fffbeb', border: '1px solid #fde68a', padding: '14px', borderRadius: '6px' }}>
                  <div style={{ fontWeight: 600, color: '#92400e', marginBottom: '6px' }}>
                    Reason for {currentAction} (Required):
                  </div>
                  <input
                    type="text"
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="Enter formal justification reason..."
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '4px', border: '1px solid #cbd5e1', marginBottom: '8px' }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => handleTransition(currentAction)} style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}>
                      Confirm Transition
                    </button>
                    <button onClick={() => setCurrentAction(null)} style={{ background: '#cbd5e1', color: '#334155', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Linked Corrective Action */}
              {occ.correctiveActionNumber && (
                <div style={{
                  background: '#f0fdf4',
                  border: '1.5px solid #86efac',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <div>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Linked Corrective Action (CAPA)
                    </span>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#166534', fontFamily: 'monospace', marginTop: '2px' }}>
                      {occ.correctiveActionNumber} {occ.correctiveActionTitle ? `— ${occ.correctiveActionTitle}` : ''}
                    </div>
                    <div style={{ fontSize: '12px', color: '#15803d', marginTop: '3px' }}>
                      Department: <strong>{occ.correctiveActionDepartment || 'Assigned'}</strong>
                      {occ.correctiveActionDueDate ? ` • Due: ${new Date(occ.correctiveActionDueDate).toLocaleDateString()}` : ''}
                      {occ.correctiveActionPriority ? ` • Priority: ${occ.correctiveActionPriority}` : ''}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    background: '#dcfce7',
                    color: '#15803d',
                    padding: '4px 10px',
                    borderRadius: '999px',
                    border: '1px solid #86efac',
                  }}>
                    Shared CAPA
                  </div>
                </div>
              )}

              {/* Grid details */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>Event Date (UTC)</div>
                  <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>{new Date(occ.eventDate).toLocaleString()}</div>
                </div>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>Severity Level</div>
                  <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>{occ.severity}</div>
                </div>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>Reporting Regime</div>
                  <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>{occ.isMandatory ? 'Mandatory (MOR)' : 'Voluntary (VOR)'}</div>
                </div>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>Flight / Aircraft</div>
                  <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>{occ.flightNumber || 'N/A'} / {occ.aircraftReg || 'N/A'}</div>
                </div>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>Location / Aerodrome</div>
                  <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>{occ.location || 'N/A'}</div>
                </div>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>Category / ICAO Code</div>
                  <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>{occ.categoryName} ({occ.icaoCode || 'ADREP'})</div>
                </div>
              </div>

              {/* Narrative */}
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '8px' }}>Description & Safety Narrative</div>
                <p style={{ margin: 0, lineHeight: 1.6, color: '#334155' }}>{occ.description}</p>
              </div>

              {/* Regulatory 72h MOR Countdown Box */}
              {occ.deadlines && occ.deadlines.length > 0 && (
                <div style={{ marginTop: '20px', padding: '16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f1f5f9' }}>
                  <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>Regulatory 72-Hour Compliance Status</div>
                  {occ.deadlines.map((dl) => (
                    <div key={dl.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span>Authority: <strong>{dl.regulatoryBody}</strong> | Deadline UTC: {new Date(dl.deadlineUtc).toLocaleString()}</span>
                      </div>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 700,
                        background: dl.status === 'MET' ? '#dcfce7' : dl.status === 'OVERDUE' ? '#fee2e2' : '#fef3c7',
                        color: dl.status === 'MET' ? '#15803d' : dl.status === 'OVERDUE' ? '#b91c1c' : '#b45309'
                      }}>
                        {dl.status} {dl.remainingHours !== undefined ? `(${dl.remainingHours}h remaining)` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'reports' && (
            <div>
              <div style={{ marginBottom: '16px', fontWeight: 600, color: '#1e293b' }}>
                Correlated Raw Reports (1..N Multi-Report Intake)
              </div>
              <form onSubmit={handleLinkReport} style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Report ID (e.g. REP-101)"
                  value={newReportId}
                  onChange={(e) => setNewReportId(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #cbd5e1', flex: 1 }}
                  required
                />
                <input
                  type="text"
                  placeholder="Report Number (e.g. REP-2026-0045)"
                  value={newReportNumber}
                  onChange={(e) => setNewReportNumber(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #cbd5e1', flex: 1 }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                  <input
                    type="checkbox"
                    checked={isPrimaryReport}
                    onChange={(e) => setIsPrimaryReport(e.target.checked)}
                  />
                  Is Primary
                </label>
                <button type="submit" style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer' }}>
                  Link Report
                </button>
              </form>

              {(!occ.linkedReports || occ.linkedReports.length === 0) ? (
                <div style={{ color: '#64748b', padding: '20px', textAlign: 'center' }}>No raw reports linked yet.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                      <SortableTh field="reportNumber" sortField={repSortField} sortDirection={repSortDirection} onSort={requestRepSort} style={{ padding: '8px' }}>
                        Rapor No / ID
                      </SortableTh>
                      <SortableTh field="title" sortField={repSortField} sortDirection={repSortDirection} onSort={requestRepSort} style={{ padding: '8px' }}>
                        Rapor Başlığı
                      </SortableTh>
                      <SortableTh field="severity" sortField={repSortField} sortDirection={repSortDirection} onSort={requestRepSort} style={{ padding: '8px' }}>
                        Önem / Durum
                      </SortableTh>
                      <SortableTh field="isPrimary" sortField={repSortField} sortDirection={repSortDirection} onSort={requestRepSort} style={{ padding: '8px' }}>
                        Rol
                      </SortableTh>
                      <SortableTh field="linkedAt" sortField={repSortField} sortDirection={repSortDirection} onSort={requestRepSort} style={{ padding: '8px' }}>
                        Bağlanma Tarihi
                      </SortableTh>
                      <th style={{ padding: '8px', textAlign: 'right' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLinkedReports.map((r) => (
                      <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px' }}>
                          <button
                            type="button"
                            onClick={() => handleOpenReportDetails(r.reportId || r.reportNumber)}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              fontFamily: 'monospace',
                              fontWeight: 700,
                              color: '#2563eb',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              textAlign: 'left',
                            }}
                            title="Rapor detaylarını görüntülemek için tıklayın"
                          >
                            {r.reportNumber || r.reportId}
                          </button>
                        </td>
                        <td style={{ padding: '8px', maxWidth: '240px' }}>
                          <span
                            onClick={() => handleOpenReportDetails(r.reportId || r.reportNumber)}
                            style={{
                              color: '#1e293b',
                              fontWeight: 500,
                              cursor: 'pointer',
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={r.title || 'Rapor detaylarını görüntülemek için tıklayın'}
                          >
                            {r.title || 'Safety Report'}
                          </span>
                        </td>
                        <td style={{ padding: '8px' }}>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            {r.severity && (
                              <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', background: '#f1f5f9', color: '#475569' }}>
                                {r.severity}
                              </span>
                            )}
                            {r.status && (
                              <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', background: '#e0f2fe', color: '#0369a1' }}>
                                {r.status}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '8px' }}>
                          {r.isPrimary ? (
                            <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                              PRIMARY
                            </span>
                          ) : (
                            <span style={{ color: '#64748b', fontSize: '12px' }}>Secondary</span>
                          )}
                        </td>
                        <td style={{ padding: '8px', color: '#64748b' }}>{new Date(r.linkedAt).toLocaleDateString()}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <button
                              type="button"
                              onClick={() => handleOpenReportDetails(r.reportId || r.reportNumber)}
                              style={{
                                background: '#eff6ff',
                                color: '#1d4ed8',
                                border: '1px solid #bfdbfe',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                              title="Rapor bilgilerini modalda aç"
                            >
                              <Eye size={12} /> Bilgileri Aç
                            </button>
                            <button
                              type="button"
                              onClick={() => handleNavigateToSafetyReports(r.reportId || r.reportNumber)}
                              style={{
                                background: '#f8fafc',
                                color: '#0f172a',
                                border: '1px solid #cbd5e1',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                              title="Safety Reports modülünde bu rapora git"
                            >
                              <ExternalLink size={12} /> Safety Reports ↗
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUnlinkReport(r.reportId)}
                              style={{ background: 'transparent', color: '#ef4444', border: 'none', cursor: 'pointer', fontSize: '12px', marginLeft: '4px' }}
                              title="Bağlantıyı kaldır"
                            >
                              Unlink
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'assignments' && (
            <div>
              <div style={{ marginBottom: '16px', fontWeight: 600, color: '#1e293b' }}>
                Department Routing & Investigator Assignment
              </div>
              <form onSubmit={handleAddAssignment} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '8px', marginBottom: '16px' }}>
                <input
                  type="text"
                  placeholder="Investigator ID"
                  value={assignUser}
                  onChange={(e) => setAssignUser(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
                <input
                  type="text"
                  placeholder="Full Name"
                  value={assignName}
                  onChange={(e) => setAssignName(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                  required
                />
                <select value={assignDept} onChange={(e) => setAssignDept(e.target.value)} style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                  <option value="Flight Operations">Flight Operations</option>
                  <option value="Safety & Quality">Safety & Quality</option>
                  <option value="Maintenance / CAMO">Maintenance / CAMO</option>
                  <option value="Ground Operations">Ground Operations</option>
                </select>
                <select value={assignRole} onChange={(e) => setAssignRole(e.target.value)} style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                  <option value="LEAD_INVESTIGATOR">Lead Investigator</option>
                  <option value="TECHNICAL_ADVISOR">Technical Advisor</option>
                  <option value="SAFETY_OFFICER">Safety Officer</option>
                </select>
                <button type="submit" style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer' }}>
                  Assign
                </button>
              </form>

              {(!occ.assignments || occ.assignments.length === 0) ? (
                <div style={{ color: '#64748b', padding: '20px', textAlign: 'center' }}>No investigator assigned yet.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                      <th style={{ padding: '8px' }}>Name</th>
                      <th style={{ padding: '8px' }}>Department</th>
                      <th style={{ padding: '8px' }}>Role</th>
                      <th style={{ padding: '8px' }}>Assigned Date</th>
                      <th style={{ padding: '8px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {occ.assignments.map((a) => (
                      <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px', fontWeight: 600 }}>{a.assignedToName}</td>
                        <td style={{ padding: '8px' }}>{a.department}</td>
                        <td style={{ padding: '8px' }}>{a.role}</td>
                        <td style={{ padding: '8px', color: '#64748b' }}>{new Date(a.assignedAt).toLocaleDateString()}</td>
                        <td style={{ padding: '8px' }}>
                          <span style={{ background: '#dcfce7', color: '#15803d', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                            {a.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'evidence' && (
            <div>
              <div style={{ marginBottom: '16px', fontWeight: 600, color: '#1e293b' }}>
                Evidence Files, FDR Logs & Photos
              </div>
              <form onSubmit={handleAddEvidence} style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                <input
                  type="text"
                  placeholder="File name (e.g. cockpit_log.pdf)"
                  value={evidenceName}
                  onChange={(e) => setEvidenceName(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #cbd5e1', flex: 1 }}
                  required
                />
                <input
                  type="text"
                  placeholder="Storage key (optional)"
                  value={evidenceKey}
                  onChange={(e) => setEvidenceKey(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #cbd5e1', flex: 1 }}
                />
                <button type="submit" style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer' }}>
                  Upload Metadata
                </button>
              </form>

              {(!occ.evidence || occ.evidence.length === 0) ? (
                <div style={{ color: '#64748b', padding: '20px', textAlign: 'center' }}>No evidence files attached yet.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                      <th style={{ padding: '8px' }}>File Name</th>
                      <th style={{ padding: '8px' }}>MIME Type</th>
                      <th style={{ padding: '8px' }}>Storage Key</th>
                      <th style={{ padding: '8px' }}>Uploaded At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {occ.evidence.map((ev) => (
                      <tr key={ev.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px', fontWeight: 600 }}>{ev.fileName}</td>
                        <td style={{ padding: '8px', color: '#64748b' }}>{ev.mimeType}</td>
                        <td style={{ padding: '8px', color: '#475569', fontFamily: 'monospace', fontSize: '11px' }}>{ev.storageKey}</td>
                        <td style={{ padding: '8px', color: '#64748b' }}>{new Date(ev.uploadedAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'comments' && (
            <div>
              <div style={{ marginBottom: '16px', fontWeight: 600, color: '#1e293b' }}>
                Internal Safety Review & Discussion Thread
              </div>
              <form onSubmit={handleAddComment} style={{ marginBottom: '16px' }}>
                <textarea
                  rows={3}
                  placeholder="Add an internal review comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '4px', border: '1px solid #cbd5e1', marginBottom: '8px' }}
                  required
                />
                <button type="submit" style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer' }}>
                  Post Comment
                </button>
              </form>

              {(!occ.comments || occ.comments.length === 0) ? (
                <div style={{ color: '#64748b', padding: '20px', textAlign: 'center' }}>No comments yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {occ.comments.map((c) => (
                    <div key={c.id} style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{c.userName}</span>
                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>{new Date(c.createdAt).toLocaleString()}</span>
                      </div>
                      <p style={{ margin: 0, color: '#334155', lineHeight: 1.5 }}>{c.commentText}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'timeline' && (
            <div>
              <div style={{ marginBottom: '16px', fontWeight: 600, color: '#1e293b' }}>
                Occurrence Lifecycle State Transition History
              </div>
              {(!occ.history || occ.history.length === 0) ? (
                <div style={{ color: '#64748b', padding: '20px', textAlign: 'center' }}>No state transition history.</div>
              ) : (
                <div style={{ borderLeft: '2px solid #0284c7', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {occ.history.map((h) => (
                    <div key={h.id} style={{ position: 'relative' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                        <span style={{ fontWeight: 700, color: '#0284c7' }}>{h.fromStatus} → {h.toStatus}</span>
                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>by {h.changedBy} at {new Date(h.changedAt).toLocaleString()}</span>
                      </div>
                      {h.reason && (
                        <div style={{ color: '#475569', fontSize: '12px', background: '#f8fafc', padding: '6px 10px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                          Reason: {h.reason}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'eccairs' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>EASA / ICAO ECCAIRS 2 Standard XML Output</span>
                <button
                  onClick={loadEccairsXml}
                  style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                >
                  Reload XML
                </button>
              </div>
              {eccairsLoading ? (
                <div>Generating ECCAIRS payload...</div>
              ) : (
                <pre style={{ background: '#0f172a', color: '#38bdf8', padding: '16px', borderRadius: '6px', fontSize: '12px', overflowX: 'auto', maxHeight: '400px' }}>
                  {eccairsXml}
                </pre>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: '#e2e8f0', color: '#334155', border: 'none', padding: '6px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
            Close
          </button>
        </div>
      </div>

      {/* Linked Report Inspection Modal */}
      {inspectingReport && (
        <ReportDetailsModal
          isOpen={!!inspectingReport}
          report={inspectingReport}
          onClose={() => setInspectingReport(null)}
          onNavigateToIngestion={(targetRepId) => {
            setInspectingReport(null);
            onClose();
            handleNavigateToSafetyReports(targetRepId);
          }}
          onReportUpdated={() => {
            fetchDetail();
          }}
        />
      )}
    </div>
  );
};
